import { useState, useEffect } from 'react';
import { Funcionario, FuncionarioFormData, ApiResponse } from '../types';
import { 
  createFuncionario, 
  getFuncionarios, 
  updateFuncionario, 
  deleteFuncionario 
} from '../services/funcionarioService';
import { toast } from 'react-toastify';

export const useFuncionarios = (userId: string) => {
  const [funcionarios, setFuncionarios] = useState<Funcionario[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');

  const loadFuncionarios = async () => {
    try {
      setLoading(true);
      setError('');
      const result = await getFuncionarios(userId);
      
      if (result.success && result.data) {
        setFuncionarios(result.data);
      } else {
        setError(result.error || 'Erro ao carregar funcionários');
        toast.error(result.error || 'Erro ao carregar funcionários');
      }
    } catch (err) {
      const errorMessage = 'Erro ao carregar funcionários';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const addFuncionario = async (funcionarioData: FuncionarioFormData): Promise<ApiResponse<Funcionario>> => {
    try {
      setError('');
      const result = await createFuncionario(funcionarioData, userId);
      
      if (result.success && result.data) {
        setFuncionarios(prev => [result.data!, ...prev]);
        toast.success('Funcionário criado com sucesso!');
        return { success: true, data: result.data };
      } else {
        const errorMessage = result.error || 'Erro ao criar funcionário';
        setError(errorMessage);
        toast.error(errorMessage);
        return { success: false, error: errorMessage };
      }
    } catch (err) {
      const errorMessage = 'Erro ao criar funcionário';
      setError(errorMessage);
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const toggleFuncionarioStatus = async (id: string, ativo: boolean) => {
    try {
      setError('');
      const result = await updateFuncionario(id, { ativo });
      
      if (result.success) {
        setFuncionarios(prev => 
          prev.map(func => 
            func.id === id ? { ...func, ativo } : func
          )
        );
        toast.success(`Funcionário ${ativo ? 'ativado' : 'desativado'} com sucesso!`);
        return { success: true };
      } else {
        const errorMessage = result.error || 'Erro ao atualizar funcionário';
        setError(errorMessage);
        toast.error(errorMessage);
        return { success: false, error: errorMessage };
      }
    } catch (err) {
      const errorMessage = 'Erro ao atualizar funcionário';
      setError(errorMessage);
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const removeFuncionario = async (id: string) => {
    try {
      setError('');
      const result = await deleteFuncionario(id);
      
      if (result.success) {
        setFuncionarios(prev => prev.filter(func => func.id !== id));
        toast.success('Funcionário removido com sucesso!');
        return { success: true };
      } else {
        const errorMessage = result.error || 'Erro ao remover funcionário';
        setError(errorMessage);
        toast.error(errorMessage);
        return { success: false, error: errorMessage };
      }
    } catch (err) {
      const errorMessage = 'Erro ao remover funcionário';
      setError(errorMessage);
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  useEffect(() => {
    if (userId) {
      loadFuncionarios();
    }
  }, [userId]);

  return {
    funcionarios,
    loading,
    error,
    loadFuncionarios,
    addFuncionario,
    toggleFuncionarioStatus,
    removeFuncionario
  };
};
